import {MetaModuleType} from "@hooks/WorkWithJson/GetComponentDataByKey/GetComponentDataByKeyTypes"
import Head from "next/head"
import {useRouter} from "next/router"
import React from "react"

const HeadMeta = ({
	meta,
	customPath,
}: {
	meta?: MetaModuleType
	customPath?: string
}) => {
	const router = useRouter()

	const {
		locale = "en",
		defaultLocale = "en",
		locales = ["en", "fi", "se", "da", "no", "de", "nl", "is"],
	} = router

	const baseDomain = "https://dreambroker.com"
	const normalizedPath = (customPath || router.asPath || "/").startsWith("/")
		? customPath || router.asPath || "/"
		: `/${customPath || router.asPath || "/"}`

	const canonicalUrl =
		locale === defaultLocale
			? `${baseDomain}${normalizedPath}`
			: `${baseDomain}/${locale}${normalizedPath}`

	const metaTitle =
		meta?.title || "Dream Broker – Communication Software Company"
	const metaDescription =
		meta?.description ||
		"Dream Broker helps people and enterprises to communicate."
	const metaKeywords =
		meta?.keywords ||
		"video communication, enterprise video, online video platform"
	const metaAuthor = meta?.author || "Dream Broker Ltd"
	const metaOgTitle = meta?.ogTitle || metaTitle
	const metaOgType =
		meta?.ogType?.toLowerCase() === "article" ? "article" : "website"
	const metaOgDescription = meta?.ogDescription || metaDescription
	const metaOgImage = meta?.ogImage
		? `${baseDomain}${meta.ogImage}`
		: `${baseDomain}/DB.jpg`

	return (
		<Head>
			{/* Basic Meta */}
			<title>{metaTitle}</title>
			<meta name={"title"} content={metaTitle} />
			<meta name={"description"} content={metaDescription} />
			<meta name={"keywords"} content={metaKeywords} />
			<meta name={"author"} content={metaAuthor} />
			<meta
				name={"viewport"}
				content={"width=device-width, initial-scale=1"}
			/>

			{/* Open Graph */}
			<meta property={"og:title"} content={metaOgTitle} />
			<meta property={"og:type"} content={metaOgType} />
			<meta property={"og:description"} content={metaOgDescription} />
			<meta property={"og:image"} content={metaOgImage} />
			<meta property={"og:url"} content={canonicalUrl} />
			<meta property={"og:site_name"} content={"Dream Broker"} />

			{/* Twitter */}
			<meta name={"twitter:card"} content={"summary_large_image"} />
			<meta name={"twitter:site"} content={"@DreamBrokerLtd"} />
			<meta name={"twitter:creator"} content={"@DreamBrokerLtd"} />
			<meta name={"twitter:title"} content={metaOgTitle} />
			<meta name={"twitter:description"} content={metaOgDescription} />
			<meta name={"twitter:image"} content={metaOgImage} />

			{/* Canonical */}
			<link rel={"canonical"} href={canonicalUrl} />

			{/* Hreflang */}
			{locales.map((loc) => {
				const href =
					loc === defaultLocale
						? `${baseDomain}${normalizedPath}`
						: `${baseDomain}/${loc}${normalizedPath}`
				return (
					<link
						key={loc}
						rel={"alternate"}
						hrefLang={loc}
						href={href}
					/>
				)
			})}
			<link
				rel={"alternate"}
				hrefLang={"x-default"}
				href={`${baseDomain}${normalizedPath}`}
			/>

			{/* Favicon */}
			<link rel={"icon"} href={"/favicon.ico?v=2"} />
		</Head>
	)
}

export default HeadMeta
